import React, { useId, useMemo, useState } from "react";
import { IconSearch, IconX, IconCheck } from "@tabler/icons-react";
import "./treatment-picker.scss";
import { formatRM } from "../../utils/format";

// Multi-select for the treatments planned in one visit. Selected items show as removable chips;
// the list stays open while ticking so several can be picked in a row.
const TreatmentPicker = ({ treatments, value, onChange, label = "Treatments" }) => {
  const id = useId();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [category, setCategory] = useState("All");

  const byId = useMemo(() => Object.fromEntries(treatments.map((t) => [t.id, t])), [treatments]);
  const selected = value.map((tid) => byId[tid]).filter(Boolean);
  const totalMinutes = selected.reduce((s, t) => s + t.duration, 0);
  const totalPrice = selected.reduce((s, t) => s + t.price, 0);

  const categories = useMemo(() => ["All", ...new Set(treatments.map((t) => t.category))], [treatments]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return treatments.filter(
      (t) =>
        (category === "All" || t.category === category) &&
        (!q || t.name.toLowerCase().includes(q) || t.category.toLowerCase().includes(q))
    );
  }, [treatments, query, category]);

  const toggle = (tid) => onChange(value.includes(tid) ? value.filter((v) => v !== tid) : [...value, tid]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && open && results[active]) {
      e.preventDefault();
      toggle(results[active].id);
    } else if (e.key === "Escape" && open) {
      e.stopPropagation(); // close only the list, not a surrounding dialog
      setOpen(false);
    } else if (e.key === "Backspace" && !query && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="treatment-picker">
      <label className="form-field__label" htmlFor={`${id}-input`}>
        {label}
      </label>

      {selected.length > 0 && (
        <ul className="treatment-picker__chips" aria-label="Selected treatments">
          {selected.map((t) => (
            <li className="treatment-picker__chip" key={t.id}>
              <span>
                {t.name} <span className="treatment-picker__chip-meta">{t.duration} min</span>
              </span>
              <button type="button" aria-label={`Remove ${t.name}`} onClick={() => toggle(t.id)}>
                <IconX size={14} stroke={2} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="treatment-picker__field">
        <IconSearch className="treatment-picker__icon" size={18} stroke={1.75} />
        <input
          id={`${id}-input`}
          className="form-field__control treatment-picker__input"
          type="search"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          aria-activedescendant={open && results[active] ? `${id}-opt-${results[active].id}` : undefined}
          autoComplete="off"
          placeholder={selected.length ? "Add another treatment" : "Search and select treatments"}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
        />

        {open && (
          <div className="treatment-picker__panel">
            <div className="treatment-picker__filters" role="group" aria-label="Filter by category">
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  tabIndex={-1}
                  aria-pressed={category === c}
                  className={`treatment-picker__filter ${category === c ? "treatment-picker__filter--active" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault(); // keep focus in the search box
                    setCategory(c);
                    setActive(0);
                  }}
                >
                  {c}
                </button>
              ))}
            </div>
          <ul
            className="treatment-picker__list"
            id={`${id}-list`}
            role="listbox"
            aria-multiselectable="true"
            aria-label={`${label} options`}
          >
            {results.map((t, i) => {
              const checked = value.includes(t.id);
              return (
                <li
                  key={t.id}
                  id={`${id}-opt-${t.id}`}
                  role="option"
                  aria-selected={checked}
                  className={`treatment-picker__option ${i === active ? "treatment-picker__option--active" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault(); // keep the list open for the next pick
                    toggle(t.id);
                  }}
                  onMouseEnter={() => setActive(i)}
                >
                  <span className={`treatment-picker__box ${checked ? "treatment-picker__box--checked" : ""}`} aria-hidden="true">
                    {checked && <IconCheck size={14} stroke={3} />}
                  </span>
                  <span className="treatment-picker__name">{t.name}</span>
                  <span className="treatment-picker__meta">
                    {t.duration} min · {formatRM(t.price)}
                  </span>
                </li>
              );
            })}
            {results.length === 0 && (
              <li className="treatment-picker__empty" role="presentation">
                {query ? `No ${category === "All" ? "" : `${category.toLowerCase()} `}treatment matches "${query}".` : "No treatments in this category."}
              </li>
            )}
          </ul>
          </div>
        )}
      </div>

      <span className="form-field__hint" aria-live="polite">
        {selected.length
          ? `${selected.length} ${selected.length === 1 ? "treatment" : "treatments"}, ${totalMinutes} min, ${formatRM(totalPrice)}`
          : "Pick one or more treatments for this visit."}
      </span>
    </div>
  );
};

export default TreatmentPicker;
